import { createRouter, createWebHistory } from 'vue-router'
import { ERouteNames } from '@/router/ERouteNames'
import { useUserStore } from '@/modules/user/stores/userStore'
// Стор аутентификации — проверка isAuth и restoreAuth в guard
import { useAuthStore } from '@/modules/auth/stores/authStore'
// Стор настроек — загрузка settings в guard для доступности на всех страницах
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
import { consumeAppCheckoutQuery } from '@/utils/appCheckoutFromPlay'

import LandingPage from '@/views/LandingPage.vue'
import PaymentOverlay from "@/modules/pay/components/PaymentOverlay.vue";
import AdminNavigation from "@/views/AdminNavigation.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import AdminMessages from "@/modules/admin/components/AdminMessages.vue";
import AdminUsers from "@/modules/admin/components/AdminUsers.vue";
import AdminWorks from "@/modules/admin/components/AdminWorks.vue";
import AdminServers from "@/modules/admin/components/AdminServers.vue";
import AdminStatistics from "@/modules/admin/components/AdminStatistics.vue";
import AdminSettings from "@/modules/admin/components/AdminSettings.vue";
import AdminGuides from "@/modules/admin/components/AdminGuides.vue";
import AdminTransactions from "@/modules/admin/components/AdminTransactions.vue";
import AdminConsole from "@/modules/admin/components/AdminConsole.vue";
import AuthPage from "@/views/AuthPage.vue";
import ProfilePage from "@/views/ProfilePage.vue";
import MagicLinkPage from "@/views/MagicLinkPage.vue";
import LeaderboardPage from "@/views/LeaderboardPage.vue";

const routes = [
  {
    path: '/',
    name: ERouteNames.HOME,
    component: LandingPage,
  },
  {
    path: '/payment',
    name: ERouteNames.PAYMENT,
    component: PaymentOverlay,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: ERouteNames.ADMIN_NAV,
    component: AdminNavigation,
    meta: { requiresAuth: true, requiresAdmin: true },
    },
  {
        path: '/admin/servers',
        name: ERouteNames.SERVERS,
        component: AdminServers,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/users',
        name: ERouteNames.USERS,
        component: AdminUsers,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/messages',
        name: ERouteNames.MESSAGES,
        component: AdminMessages,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/works',
        name: ERouteNames.WORKS,
        component: AdminWorks,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/statistics',
        name: ERouteNames.STATISTICS,
        component: AdminStatistics,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/settings',
        name: ERouteNames.SETTINGS,
        component: AdminSettings,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/guides',
        name: ERouteNames.GUIDES,
        component: AdminGuides,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/transactions',
        name: ERouteNames.TRANSACTIONS,
        component: AdminTransactions,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
        path: '/admin/console',
        name: ERouteNames.CONSOLE,
        component: AdminConsole,
        meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/auth',
    name: ERouteNames.AUTH,
    component: AuthPage,
  },
  {
    path: '/auth/magic-link',
    name: ERouteNames.MAGIC_LINK,
    component: MagicLinkPage,
  },
  {
    path: '/profile',
    name: ERouteNames.PROFILE,
    component: ProfilePage,
    meta: { requiresAuth: true },
  },
  {
    path: '/leaderboard',
    name: ERouteNames.LEADERBOARD,
    component: LeaderboardPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: ERouteNames.NOT_FOUND,
    component: PageNotFound,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  // Логируем текущий маршрут
  console.log('[ROUTER] переход на', to.name)

  // Вход из приложения: JWT и тариф в query, без from=app (тот сценарий обрабатывает AuthPage)
  const cleanedQuery = await consumeAppCheckoutQuery(to.query)
  if (cleanedQuery) {
    return { path: to.path, query: cleanedQuery, hash: to.hash, replace: true }
  }

  // 1. Проверка авторизации для защищённых роутов
  const needsAuth = to.meta.requiresAuth || to.meta.requiresAdmin
  if (needsAuth && !authStore.isAuth) {
    // Пробуем восстановить сессию из localStorage
    const restored = await authStore.restoreAuth()
    if (!restored) {
      console.warn('[ROUTER] нет авторизации, редирект на /auth')
      return { name: ERouteNames.AUTH }
    }
    // После восстановления auth — загружаем данные пользователя и настройки параллельно
    const settingsStore = useSettingsStore()
    await Promise.all([
      userStore.isUserData ? Promise.resolve() : userStore.getUserData(),
      settingsStore.isLoaded ? Promise.resolve() : settingsStore.getSettings(),
    ])
  }

  // 2. Проверка прав админа
  if (to.meta.requiresAdmin && !userStore.user?.isAdmin) {
    console.warn('[ROUTER] доступ к админке запрещён, редирект на /')
    return { name: ERouteNames.HOME }
  }
})

export default router
